import React from 'react';

export type FundraisingType =
  | 'VirtualWalkRun'
  | 'TshirtSale'
  | 'Sponsorship'
  | 'Donation'
  | 'GeneralOther';

export interface EntryColumn {
  key: string;
  label: string;
  format?: 'currency' | 'date';
}

export const ENTRY_COLUMNS: Record<FundraisingType, EntryColumn[]> = {
  VirtualWalkRun: [
    { key: 'participantName', label: 'Participant Name' },
    { key: 'sponsorName', label: 'Sponsor Name' },
    { key: 'milesSponsored', label: 'Miles Sponsored' },
    { key: 'revenue', label: 'Revenue', format: 'currency' },
    { key: 'sponsoredDate', label: 'Sponsored Date', format: 'date' },
    { key: 'entryDate', label: 'Entry Date', format: 'date' },
  ],
  TshirtSale: [
    { key: 'size', label: 'Size' },
    { key: 'style', label: 'Style' },
    { key: 'quantitySold', label: 'Qty' },
    { key: 'pricePerItem', label: 'Price', format: 'currency' },
    { key: 'total', label: 'Total', format: 'currency' },
  ],
  Sponsorship: [
    { key: 'sponsorName', label: 'Sponsor' },
    { key: 'tier', label: 'Tier' },
    { key: 'pledgedAmount', label: 'Pledged', format: 'currency' },
    { key: 'receivedAmount', label: 'Received', format: 'currency' },
  ],
  Donation: [
    { key: 'donorName', label: 'Donor' },
    { key: 'donationAmount', label: 'Amount', format: 'currency' },
    { key: 'donationDate', label: 'Date', format: 'date' },
  ],
  GeneralOther: [
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', format: 'currency' },
  ],
};

export interface EntriesPanelProps {
  type: FundraisingType;
  entries: Record<string, unknown>[];
  loading: boolean;
}

function formatCurrency(value: unknown): string {
  const num = Number(value);
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: unknown): string {
  return new Date(value as string).toLocaleDateString();
}

function formatCell(value: unknown, column: EntryColumn): string {
  if (column.key === 'donorName' && (value === null || value === undefined)) {
    return 'Anonymous';
  }
  if (column.format === 'currency') {
    return formatCurrency(value);
  }
  if (column.format === 'date') {
    return formatDate(value);
  }
  return String(value ?? '');
}

const panelStyles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#0f2d1f',
    fontSize: '0.85rem',
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    color: '#333',
  },
  loading: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#666',
    fontSize: '0.85rem',
  },
  empty: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#666',
    fontSize: '0.85rem',
  },
};

export function EntriesPanel({ type, entries, loading }: EntriesPanelProps) {
  if (loading) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.loading}>Loading entries...</div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.empty}>No entries yet.</div>
      </div>
    );
  }

  const columns = ENTRY_COLUMNS[type];

  return (
    <div style={panelStyles.container}>
      <table style={panelStyles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={panelStyles.th}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => (
            <tr key={(entry.id as string) ?? idx}>
              {columns.map((col) => (
                <td key={col.key} style={panelStyles.td}>
                  {formatCell(entry[col.key], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
